<template>
  <DialogRoot :open="Boolean(media)" @update:open="handleOpenChange">
    <DialogPortal>
      <DialogOverlay class="media-dialog-overlay" />
      <DialogContent
        v-if="media"
        class="media-dialog"
        :aria-describedby="undefined"
        @close-auto-focus.prevent="emit('closed')"
      >
        <div
          class="media-dialog-stage"
          @ended.capture="handlePlaybackChange(false)"
          @focusin="revealChrome"
          @pointerdown="revealChrome"
          @pointerenter="handlePointerActivity"
          @pointerleave="handlePointerLeave"
          @pointermove="handlePointerActivity"
          @pause.capture="handlePlaybackChange(false)"
          @play.capture="handlePlaybackChange(true)"
        >
          <media-player :key="media.id" :media="media" autoplay />

          <div class="media-dialog-chrome" :class="{ 'is-hidden': !isChromeVisible }">
            <DialogTitle class="media-dialog-title">{{ media.title }}</DialogTitle>
            <DialogClose class="media-dialog-close" aria-label="关闭播放">
              <i class="i-lucide-x" aria-hidden="true"></i>
            </DialogClose>
          </div>
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>

<script setup lang="ts">
  import type { MediaItem } from '~/types/site-api'

  import {
    DialogClose,
    DialogContent,
    DialogOverlay,
    DialogPortal,
    DialogRoot,
    DialogTitle,
  } from 'reka-ui'

  defineOptions({ name: 'MediaPlayerDialog' })

  const props = defineProps<{ media?: MediaItem }>()
  const emit = defineEmits<{ close: []; closed: [] }>()

  /** 播放中无操作时，悬浮标题与关闭按钮自动收起的延时（毫秒）。 */
  const CHROME_HIDE_DELAY = 2600

  const isChromeVisible = shallowRef(true)
  const isPointerInside = shallowRef(false)
  const isPlaying = shallowRef(false)
  let hideTimer: ReturnType<typeof setTimeout> | undefined

  /** 清除悬浮控件的自动收起倒计时。 */
  function clearHideTimer() {
    if (hideTimer === undefined) return
    clearTimeout(hideTimer)
    hideTimer = undefined
  }

  /** 展示悬浮标题与关闭按钮，并停止收起倒计时。 */
  function revealChrome() {
    isChromeVisible.value = true
    clearHideTimer()
  }

  /** 播放中且指针不在播放区域内时，延时收起悬浮控件。 */
  function scheduleChromeHide() {
    clearHideTimer()
    if (!isPlaying.value || isPointerInside.value) return
    hideTimer = setTimeout(() => {
      hideTimer = undefined
      isChromeVisible.value = false
    }, CHROME_HIDE_DELAY)
  }

  /** 指针位于播放区域内时，悬浮控件保持常驻。 */
  function handlePointerActivity() {
    isPointerInside.value = true
    revealChrome()
  }

  /** 指针离开播放区域后开始倒计时，是否真的收起由播放状态决定。 */
  function handlePointerLeave() {
    isPointerInside.value = false
    scheduleChromeHide()
  }

  /** 播放中才自动收起；暂停或播放结束后保持常驻，避免找不到标题与关闭入口。 */
  function handlePlaybackChange(playing: boolean) {
    isPlaying.value = playing
    if (playing) {
      scheduleChromeHide()
      return
    }
    revealChrome()
  }

  /** 关闭弹窗并通知父组件清理当前媒资。 */
  function handleOpenChange(open: boolean) {
    if (!open) emit('close')
  }

  watch(
    () => props.media,
    () => {
      clearHideTimer()
      isChromeVisible.value = true
      isPlaying.value = false
      isPointerInside.value = false
    }
  )

  onBeforeUnmount(clearHideTimer)
</script>

<style scoped lang="scss">
  .media-dialog-overlay {
    position: fixed;
    inset: 0;
    z-index: 79;
    background: rgb(24 12 14 / 78%);
  }

  .media-dialog {
    position: fixed;
    inset: 0;
    z-index: 80;
    width: min(960px, calc(100% - 32px));
    height: fit-content;
    max-height: calc(100dvh - 32px);
    margin: auto;
    overflow-y: auto;
    color: var(--ink);
    background: var(--surface);
    border-radius: 8px;
    box-shadow: var(--shadow-modal);
  }

  .media-dialog-stage {
    position: relative;
  }

  .media-dialog-chrome {
    position: absolute;
    inset: 0 0 auto;
    display: flex;
    gap: 12px;
    align-items: flex-start;
    justify-content: space-between;
    padding: 14px 16px 32px;
    pointer-events: none;
    background: linear-gradient(180deg, rgb(0 0 0 / 66%) 0%, rgb(0 0 0 / 0%) 100%);
    opacity: 1;
    transition: opacity 320ms var(--ease);
  }

  .media-dialog-chrome.is-hidden {
    opacity: 0;
  }

  .media-dialog-chrome.is-hidden .media-dialog-close {
    pointer-events: none;
  }

  .media-dialog-title {
    min-width: 0;
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.5;
    color: rgb(255 255 255 / 92%);
    overflow-wrap: anywhere;
    text-shadow: 0 1px 3px rgb(0 0 0 / 45%);
  }

  .media-dialog-close {
    display: grid;
    flex: 0 0 40px;
    place-items: center;
    width: 40px;
    height: 40px;
    padding: 0;
    color: rgb(255 255 255 / 92%);
    pointer-events: auto;
    cursor: pointer;
    background: rgb(0 0 0 / 30%);
    border: 0;
    border-radius: 50%;
    transition:
      color var(--fast) var(--ease),
      background var(--fast) var(--ease);
  }

  .media-dialog-close:hover {
    color: var(--white);
    background: rgb(0 0 0 / 55%);
  }

  .media-dialog-close:focus-visible {
    outline: 2px solid var(--white);
    outline-offset: 2px;
  }

  .media-dialog-close i {
    width: 22px;
    height: 22px;
  }

  .media-dialog :deep(.media-player) {
    border: 0;
    border-radius: 0;
  }

  @media (max-width: 767px) {
    .media-dialog-chrome {
      padding: 10px 12px 26px;
    }

    .media-dialog-title {
      font-size: 16px;
    }

    .media-dialog-close {
      flex-basis: 36px;
      width: 36px;
      height: 36px;
    }

    .media-dialog-close i {
      width: 20px;
      height: 20px;
    }
  }
</style>
